/**
 * Event Card Component
 * Displays a single event in the Upcoming Events grid
 */
import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Ticket, Users, Star, Heart, Share2, ExternalLink, Loader2 } from 'lucide-react';
import { addToWishlist, checkWishlistStatus, removeFromWishlist } from '../WishlistModal';

const EDMTRAIN_LOGO = "https://d2po6uops3e7id.cloudfront.net/img/logo/icon-square-transparent-bg-web.svg";

const EventCard = ({
  event,
  attendance,
  isLoadingAttendance,
  isLiked,
  likeCount,
  showReactions,
  reactionEmojis,
  onToggleGoing,
  onLike,
  onReaction,
  onToggleReactions,
  onCommunityShare,
  onDiscordShare,
  onViewInCalendar,
  formatEventDate,
  isMobile = false
}) => {
  const [inWishlist, setInWishlist] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState(null);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  // Check wishlist status on mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkWishlistStatus('event', event.id);
      setInWishlist(status.in_wishlist);
      setWishlistItemId(status.wishlist_item_id);
    };
    checkStatus();
  }, [event.id]);

  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    setWishlistLoading(true);
    
    try {
      if (inWishlist && wishlistItemId) {
        // Remove from wishlist
        const success = await removeFromWishlist(wishlistItemId);
        if (success) {
          setInWishlist(false);
          setWishlistItemId(null);
        }
      } else {
        // Add to wishlist
        const success = await addToWishlist('event', {
          id: event.id,
          name: event.name,
          date: event.start_datetime,
          location: event.location || event.venue?.city,
          venue: event.venue?.name,
          url: event.url,
          image_url: event.image_url,
          source: event.source,
          price: event.price_range?.display
        });
        if (success) {
          setInWishlist(true);
          // Refresh to get the wishlist item ID
          const status = await checkWishlistStatus('event', event.id);
          setWishlistItemId(status.wishlist_item_id);
        }
      }
    } finally {
      setWishlistLoading(false);
    }
  };

  return (
    <div 
      className="event-card relative rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.01] hover:-translate-y-1 hover:shadow-xl hover:shadow-cyan-500/20 group"
      style={{ transform: 'translateZ(0)', boxSizing: 'border-box', maxWidth: '100%' }}
    >
      {/* Gradient border wrapper */}
      <div 
        className="absolute inset-0 rounded-xl bg-gradient-to-r from-cyan-500 via-pink-500 to-cyan-500 p-[2px] opacity-70 group-hover:opacity-100 transition-opacity"
        style={{ 
          backgroundSize: '200% 100%',
          animation: 'gradientSlide 3s ease infinite'
        }}
      />
      
      <div className={`relative bg-gray-900 rounded-xl m-[2px] flex ${isMobile ? 'flex-col' : 'flex-row'} overflow-hidden ${isMobile ? 'min-h-0' : 'min-h-[180px]'}`} style={{ boxSizing: 'border-box' }}>
        {/* Event Image */}
        <div className={`${isMobile ? 'w-full h-32' : 'w-44 min-h-[180px]'} relative bg-gradient-to-br from-cyan-600/50 to-pink-600/50 flex-shrink-0 ${isMobile ? '' : 'self-stretch'}`}>
          {event.image_url ? (
            <img 
              src={event.image_url} 
              alt={event.name}
              className="w-full h-full object-cover absolute inset-0"
              loading="lazy"
              onError={(e) => { e.target.style.opacity = '0'; }}
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-gray-900 to-cyan-900 flex flex-col items-center justify-center p-3">
              <img 
                src={EDMTRAIN_LOGO} 
                alt="EDMtrain"
                className={`${isMobile ? 'w-16 h-16' : 'w-24 h-24'} object-contain flex-shrink-0`}
              />
              <span className="text-white/70 text-xs font-medium mt-2">via EDMtrain</span>
            </div>
          )}
          
          {/* Source Badge */}
          <div className="absolute top-2 left-2 bg-black/80 text-cyan-300 px-2 py-0.5 rounded text-xs font-medium">
            {event.source === 'skiddle' ? '🎭 Skiddle' : 
             event.source === 'edmtrain' ? '🎵 Edmtrain' : 
             event.source === 'eventbrite' ? '🎟️ Eventbrite' :
             '🎫 Ticketmaster'}
          </div>
          
          {!event.tickets_available && (
            <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded text-xs font-semibold">
              SOLD OUT
            </div>
          )}
        </div>
        
        {/* Event Details */}
        <div className={`flex-1 ${isMobile ? 'p-3' : 'p-4'} relative overflow-hidden`} style={{ minWidth: 0 }}>
          {/* Attendance Stats - Position differently on mobile */}
          <div className={`${isMobile ? 'flex gap-3 mb-2' : 'absolute bottom-3 right-3 flex flex-col items-end gap-0.5'} text-xs`}>
            <div className="flex items-center gap-1 text-cyan-400">
              <Users size={11} />
              <span>{attendance.beatseekers_attending} Beatseekers</span>
            </div>
            <div className="flex items-center gap-1 text-pink-400">
              <Heart size={11} />
              <span>{attendance.friends_attending} Friends</span>
            </div>
          </div>
          
          <div className={isMobile ? '' : 'pr-28'}>
            <h4 className={`${isMobile ? 'text-sm' : 'text-base'} font-bold text-white mb-1.5 line-clamp-2`}>{event.name}</h4>
            <div className={`flex items-center flex-wrap gap-x-3 gap-y-1 text-gray-300 ${isMobile ? 'text-xs' : 'text-sm'} mb-1.5`}>
              <div className="flex items-center">
                <Calendar className="mr-1 text-cyan-400" size={isMobile ? 11 : 13} />
                {formatEventDate(event.start_datetime)}
              </div>
              <div className="flex items-center">
                <MapPin className="mr-1 text-pink-400" size={isMobile ? 11 : 13} />
                <span className={isMobile ? 'truncate max-w-[150px]' : ''}>{event.location || event.venue?.city || 'Location TBA'}</span>
              </div>
            </div>
            <div className={`flex items-center gap-3 text-gray-400 ${isMobile ? 'text-xs' : 'text-xs'}`}>
              <div className="flex items-center">
                <Ticket className="mr-1 text-green-400" size={12} />
                {event.price_range?.display || (event.is_free ? 'Free' : 'See Website')}
              </div>
              {event.venue?.name && !isMobile && (
                <div className="flex items-center">
                  <Star className="mr-1 text-yellow-400" size={12} />
                  <span className="truncate max-w-[120px]">{event.venue.name}</span>
                </div>
              )}
            </div>
          </div>
          
          {event.summary && !isMobile && (
            <p className="text-gray-400 text-xs line-clamp-2 mb-2 mt-1 pr-28">{event.summary}</p>
          )}
          
          {/* Category Badges */}
          <div className={`flex flex-wrap gap-1.5 ${isMobile ? 'my-2' : 'mb-2'}`}>
            <span className={`bg-cyan-500/20 text-cyan-300 ${isMobile ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-0.5 text-xs'} rounded-full font-medium`}>
              {event.category?.name || 'Electronic'}
            </span>
            {event.organizer?.name && !isMobile && (
              <span className="bg-pink-500/20 text-pink-300 px-2 py-0.5 rounded-full text-xs font-medium truncate max-w-[150px]">
                by {event.organizer.name}
              </span>
            )}
          </div>
          
          {/* Social Actions */}
          <div className={`flex items-center gap-1.5 mb-2 flex-wrap ${isMobile ? 'text-xs' : ''}`}>
            {/* Wishlist Star */}
            <button
              onClick={handleWishlistToggle}
              disabled={wishlistLoading}
              className="transition-all hover:scale-110"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: wishlistLoading ? 'wait' : 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: wishlistLoading ? 0.5 : 1
              }}
              title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}
            >
              {wishlistLoading ? (
                <Loader2 size={isMobile ? 14 : 16} className="animate-spin text-yellow-400" />
              ) : (
                <Star 
                  size={isMobile ? 16 : 18} 
                  className={inWishlist ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400 hover:text-yellow-400'} 
                />
              )}
            </button>

            <button
              onClick={() => onLike(event.id)}
              className={`flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5' : 'px-2 py-1'} rounded-lg text-xs font-medium transition-all ${
                isLiked
                  ? 'bg-pink-500/30 text-pink-300 border border-pink-500/50'
                  : 'bg-gray-700/50 text-gray-300 border border-gray-600 hover:bg-gray-600/50'
              }`}
            >
              <Heart size={isMobile ? 10 : 12} className={isLiked ? 'fill-pink-400' : ''} />
              {likeCount}
            </button>
            
            {!isMobile && (
              <div className="relative">
                <button
                  onClick={() => onToggleReactions(event.id)}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600 hover:bg-gray-600/50 transition-all"
                >
                  <span>😀</span>
                </button>
                
                {showReactions && (
                  <div className="absolute bottom-full left-0 mb-2 flex gap-1 bg-gray-800 border border-gray-600 rounded-xl p-1.5 shadow-xl z-20">
                    {reactionEmojis.map((emoji) => (
                      <button
                        key={emoji}
                        onClick={() => onReaction(event.id, emoji)}
                        className="w-6 h-6 flex items-center justify-center hover:bg-gray-700 rounded-lg transition-all hover:scale-125 text-sm"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={() => onCommunityShare(event)}
              className={`flex items-center gap-1 ${isMobile ? 'px-1.5 py-0.5' : 'px-2 py-1'} rounded-lg text-xs font-medium bg-gray-700/50 text-gray-300 border border-gray-600 hover:bg-gray-600/50 transition-all`}
            >
              <Share2 size={isMobile ? 10 : 12} />
              {!isMobile && 'Share'}
            </button>
            
            {!isMobile && (
              <button
                onClick={() => onDiscordShare(event)}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-indigo-500/20 text-indigo-300 border border-indigo-500/50 hover:bg-indigo-500/30 transition-all"
              >
                <svg width="12" height="12" viewBox="0 0 71 55" fill="currentColor">
                  <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0834 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0581 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 48.9718 48.5383C50.038 50.6034 51.2554 52.5699 52.5959 54.435C52.6519 54.5139 52.7526 54.5477 52.845 54.5195C58.6464 52.7249 64.529 50.0174 70.6019 45.5576C70.6551 45.5182 70.6887 45.459 70.6943 45.3942C72.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.3451 34.1136 17.3451 30.1693C17.3451 26.225 20.1717 23.0133 23.7259 23.0133C27.308 23.0133 30.1626 26.2532 30.1099 30.1693C30.1099 34.1136 27.2801 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.9371 34.1136 40.9371 30.1693C40.9371 26.225 43.7637 23.0133 47.3178 23.0133C50.9 23.0133 53.7545 26.2532 53.7018 30.1693C53.7018 34.1136 50.9 37.3253 47.3178 37.3253Z"/>
                </svg>
              </button>
            )}
          </div>
          
          {/* Primary Actions */}
          <div className={`flex gap-2 items-center flex-wrap ${isMobile ? 'text-xs' : ''}`}>
            <button 
              onClick={() => onToggleGoing(event)}
              disabled={isLoadingAttendance}
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${
                attendance.is_going
                  ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                  : 'bg-gray-700/50 text-gray-300 border border-gray-600 hover:bg-gray-600/50 hover:border-green-500/50'
              }`}
            >
              {isLoadingAttendance ? (
                <Loader2 size={isMobile ? 12 : 14} className="animate-spin" />
              ) : (
                <Users size={isMobile ? 12 : 14} />
              )}
              {attendance.is_going ? (isMobile ? "Going ✓" : "I'm Going! ✓") : (isMobile ? "Going" : "I'm Going")}
            </button>
            
            <a 
              href={event.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'} rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 ${
                event.tickets_available 
                  ? 'bg-gradient-to-r from-cyan-500 to-pink-500 text-white hover:from-cyan-400 hover:to-pink-400'
                  : 'bg-gray-600 text-gray-300 cursor-not-allowed'
              }`}
            >
              <Ticket size={isMobile ? 12 : 14} />
              {event.tickets_available ? 'Tickets' : 'Sold Out'}
              {!isMobile && <ExternalLink size={12} />}
            </a>
            
            {/* View in Calendar link */}
            {attendance.is_going && !isMobile && (
              <button
                onClick={() => onViewInCalendar(event)}
                className="text-cyan-400 hover:text-cyan-300 text-xs flex items-center gap-1 transition-colors"
              >
                <Calendar size={12} />
                View in Event Calendar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventCard;
